import React, { ChangeEvent, FC, useState, useEffect } from 'react';
import { Box, Grid, TextField, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderFilter, SampleType, OrderListType } from 'models/order';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import CustomerDropDown from 'components/shared/customer-dropdown/customer-dropdown.component';
import { ClientType, LabClient } from 'models/client-directory';
import { SampleDropdown } from 'components/shared/order-filters/sample-dropdown.component';
import { OrderTagDropdown } from 'components/shared/order-filters/order-tag-dropdown.component';
import { usePauseReasons } from 'hooks/usePauseReasons';
import { TechnicianDropdown } from 'components/shared/order-filters/technician-dropdown.component';

type WorkflowOrderFiltersProps = {
    onFilterChange: (filters: OrderFilter) => void;
    initFilters: OrderFilter;
    hideClinicFilter?: boolean;
};

const STORAGE_KEY = 'step-order-filters';
// UI-only option value; sent to the backend as `isPaused: true`.
const ALL_PAUSED = '__ALL_PAUSED__';

const WorkflowOrderFilters: FC<WorkflowOrderFiltersProps> = ({ onFilterChange, initFilters, hideClinicFilter }) => {
    const currentUser = currentUserVar();
    const { t: translate } = useTranslation(['orders']);
    const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout>>();
    const [search, setSearch] = useState('');
    // DD-3391: an external user's type is lab_technician too, so the filter below
    // used to render for them and ask for the lab's user list - which no external
    // tier may read. Filtering by lab technician means nothing to them either:
    // their list is already only the orders shared with them.
    const canFilterByTechnician =
        permissions.isLabTechnician(currentUser.type) && !permissions.isExternalTechnician(currentUser.role);
    const isLabTechnician = permissions.isLabTechnician(currentUser.type);
    // Only the lab's current reasons are offered: a reason removed from settings can't be filtered on.
    const { pauseReasons, loading: pauseReasonsLoading } = usePauseReasons({ skip: !isLabTechnician });

    const [filters, setFilters] = useState({
        search: '',
        sample: 'ALL' as SampleType,
        orderListType: OrderListType.MY_ORDERS,
        clinicId: '' as string | null,
        orderTagIds: [] as string[],
        technicianId: '' as string | null,
        pauseReason: '' as string | null,
        isPaused: false as boolean | undefined,
    });

    useEffect(() => {
        if (initFilters) {
            setSearch(initFilters.search || '');
            setFilters({ ...filters, ...initFilters });
        }
    }, [initFilters]);

    const emitChange = (updated: typeof filters) => {
        const orderFilter: OrderFilter = {
            search: updated.search,
            clinicId: updated.clinicId || null,
            sample: updated.sample !== SampleType.ALL ? updated.sample : undefined,
            orderTagIds: updated.orderTagIds?.length ? updated.orderTagIds : undefined,
            technicianId: updated.technicianId || null,
            pauseReason: updated.pauseReason || null,
            isPaused: (!updated.pauseReason && updated.isPaused) || undefined,
        };
        onFilterChange(orderFilter);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(orderFilter));
    };

    const handleFilterChange = <K extends keyof typeof filters>(updates: Record<K, (typeof filters)[K]>) => {
        const updatedFilters = { ...filters, ...updates };
        setFilters(updatedFilters);
        emitChange(updatedFilters);
    };

    // A remembered filter on a reason that has since been removed or renamed would hide
    // orders behind an empty-looking dropdown, so it is dropped.
    useEffect(() => {
        if (pauseReasonsLoading || !filters.pauseReason) return;
        if (!pauseReasons.includes(filters.pauseReason)) handleFilterChange({ pauseReason: null });
    }, [pauseReasonsLoading, pauseReasons.join('\n'), filters.pauseReason]);

    const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearch(value);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        const timeout = setTimeout(() => {
            if (value.length === 0 || value.length >= 3) {
                handleFilterChange({ search: value });
            }
        }, 300);
        setSearchTimeout(timeout);
    };

    const handleLabClinicChange = (client: LabClient | null) => {
        if (client?.customer?._id) {
            handleFilterChange({ orderListType: OrderListType.ON_CLINIC, clinicId: client.customer._id as string });
            return;
        }
        handleFilterChange({ clinicId: null, orderListType: OrderListType.ALL_ORDERS });
    };

    return (
        <Box
            sx={{
                marginTop: 1,
                marginBottom: 2,
                bgcolor: 'background.paper',
                padding: 2,
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Grid container rowSpacing={1.5} columnSpacing={1}>
                <Grid item md={2} sm={6} xs={12}>
                    <TextField
                        fullWidth
                        onChange={handleTextChange}
                        name="search"
                        label={translate('orders:ORDERS_FREE_TEXT_FILTER')}
                        placeholder={translate('orders:ORDERS_FREE_TEXT_FILTER_PLACEHOLDER')}
                        value={search}
                    />
                </Grid>
                {!hideClinicFilter && permissions.isLabTechnician(currentUser.type) && (
                    <Grid item md={2} sm={6} xs={12}>
                        <CustomerDropDown
                            clientType={ClientType.CUSTOMER}
                            onCustomerSelect={handleLabClinicChange}
                            selectedCustomerId={filters.clinicId}
                        />
                    </Grid>
                )}
                <Grid item md={2} sm={6} xs={12}>
                    <SampleDropdown
                        sample={filters.sample}
                        onSampleChange={(sample: SampleType) => handleFilterChange({ sample })}
                    />
                </Grid>
                {permissions.isLabTechnician(currentUser.type) && (
                    <Grid item md={2} sm={6} xs={12}>
                        <OrderTagDropdown
                            selectedTagIds={filters.orderTagIds}
                            onTagChange={(orderTagIds) => handleFilterChange({ orderTagIds })}
                        />
                    </Grid>
                )}
                {canFilterByTechnician && (
                    <Grid item md={2} sm={6} xs={12}>
                        <TechnicianDropdown
                            technicianId={filters.technicianId}
                            onTechnicianChange={(technicianId) => handleFilterChange({ technicianId })}
                            showMyJobsOption
                        />
                    </Grid>
                )}
                {isLabTechnician && (
                    <Grid item md={2} sm={6} xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>{translate('workflow:PAUSE_REASON')}</InputLabel>
                            <Select
                                value={filters.pauseReason || (filters.isPaused ? ALL_PAUSED : '')}
                                label={translate('workflow:PAUSE_REASON')}
                                onChange={(e: SelectChangeEvent) => {
                                    const value = e.target.value;
                                    handleFilterChange(
                                        value === ALL_PAUSED
                                            ? { pauseReason: null, isPaused: true }
                                            : { pauseReason: value || null, isPaused: false },
                                    );
                                }}
                                data-test="workflow-order-filters.pause-reason-select"
                            >
                                <MenuItem value="">{translate('common:ALL')}</MenuItem>
                                <MenuItem value={ALL_PAUSED}>{translate('workflow:ALL_PAUSED')}</MenuItem>
                                {pauseReasons.map((pauseReason) => (
                                    <MenuItem key={pauseReason} value={pauseReason}>
                                        {pauseReason}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default WorkflowOrderFilters;
